import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/adminService';

const STATUS_COLORS = {
  CONFIRMED:            { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
  UNCONFIRMED:          { bg: '#fefce8', text: '#ca8a04', border: '#fde047' },
  FORCE_CHANGE_PASSWORD:{ bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' },
  RESET_REQUIRED:       { bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' },
  COMPROMISED:          { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
  ARCHIVED:             { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' },
};

const STATUS_OPTIONS = ['', 'CONFIRMED', 'UNCONFIRMED', 'FORCE_CHANGE_PASSWORD', 'RESET_REQUIRED', 'ARCHIVED'];

function StatusBadge({ status, enabled }) {
  if (!enabled) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-inter font-medium"
        style={{ backgroundColor: '#f9fafb', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '12px' }}>
        Disabled
      </span>
    );
  }
  const c = STATUS_COLORS[status] || STATUS_COLORS.ARCHIVED;
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-inter font-medium"
      style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: '12px' }}>
      {status}
    </span>
  );
}

function EditModal({ user, onClose, onSave }) {
  const [attrs, setAttrs] = useState({
    'custom:business_id':        user.business_id || '',
    'custom:admin':               user.admin || 'false',
    'custom:subscription_status': user.subscription_status || '',
    'custom:prepaid_minutes':     user.prepaid_minutes || '',
    'custom:prepaid_expiry':      user.prepaid_expiry || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    // Only send attributes that have a value (avoid sending empty strings for unset fields)
    const toSend = Object.fromEntries(
      Object.entries(attrs).filter(([, v]) => v !== '')
    );
    try {
      await onSave(toSend);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, type = 'text', options = null) => (
    <div className="mb-4" key={key}>
      <label className="block text-sm font-inter font-medium mb-1" style={{ color: '#222324' }}>
        {label}
      </label>
      {options ? (
        <select
          value={attrs[key]}
          onChange={e => setAttrs(a => ({ ...a, [key]: e.target.value }))}
          className="w-full px-3 py-2 text-sm font-inter border rounded-lg"
          style={{ borderColor: '#e5e7eb', color: '#222324' }}
        >
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input
          type={type}
          value={attrs[key]}
          onChange={e => setAttrs(a => ({ ...a, [key]: e.target.value }))}
          className="w-full px-3 py-2 text-sm font-inter border rounded-lg"
          style={{ borderColor: '#e5e7eb', color: '#222324' }}
        />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4" style={{ border: '1px solid #e5e7eb' }}>
        <div className="px-6 py-5" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <h3 className="text-lg font-montserrat font-semibold" style={{ color: '#222324' }}>
            Edit User
          </h3>
          <p className="text-sm font-inter mt-0.5" style={{ color: '#979ba4' }}>{user.email || user.username}</p>
        </div>

        <div className="px-6 py-5">
          {error && (
            <div className="mb-4 px-3 py-2 text-sm font-inter rounded-lg"
              style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
              {error}
            </div>
          )}

          {field('Business ID', 'custom:business_id')}
          {field('Admin', 'custom:admin', 'text', [
            { value: 'false', label: 'No' },
            { value: 'true',  label: 'Yes' },
          ])}
          {field('Subscription Status', 'custom:subscription_status', 'text', [
            { value: '',         label: '— not set —' },
            { value: 'active',   label: 'active' },
            { value: 'trialing', label: 'trialing' },
            { value: 'past_due', label: 'past_due' },
            { value: 'canceled', label: 'canceled' },
          ])}
          {field('Prepaid Minutes', 'custom:prepaid_minutes')}
          {field('Prepaid Expiry', 'custom:prepaid_expiry')}
        </div>

        <div className="px-6 py-4 flex justify-end space-x-3" style={{ borderTop: '1px solid #e5e7eb' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-inter font-medium rounded-lg transition-colors"
            style={{ color: '#6b7280', border: '1px solid #e5e7eb' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-inter font-medium rounded-lg text-white transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#055efd' }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserManager() {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(location.state?.searchEmail || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // username of user being acted on
  const [editingUser, setEditingUser] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const { signOut } = useAuth();

  const loadUsers = useCallback(async (q = search, s = statusFilter) => {
    setLoading(true);
    setError('');
    try {
      const data = await adminService.listCognitoUsers(q, s);
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadUsers('', '');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    loadUsers(search, statusFilter);
  };

  const handleToggleEnabled = async (user) => {
    if (!window.confirm(`${user.enabled ? 'Disable' : 'Enable'} ${user.email || user.username}?`)) return;
    setActionLoading(user.username);
    try {
      if (user.enabled) {
        await adminService.disableCognitoUser(user.username);
      } else {
        await adminService.enableCognitoUser(user.username);
      }
      setUsers(prev => prev.map(u =>
        u.username === user.username ? { ...u, enabled: !u.enabled } : u
      ));
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Permanently delete ${user.email || user.username}? This cannot be undone.`)) return;
    setActionLoading(user.username);
    try {
      await adminService.deleteCognitoUser(user.username);
      setUsers(prev => prev.filter(u => u.username !== user.username));
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveAttributes = async (attributes) => {
    await adminService.updateCognitoUserAttributes(editingUser.username, attributes);
    // Refresh the user in-list
    setUsers(prev => prev.map(u => {
      if (u.username !== editingUser.username) return u;
      return {
        ...u,
        business_id:         attributes['custom:business_id']        ?? u.business_id,
        admin:               attributes['custom:admin']               ?? u.admin,
        subscription_status: attributes['custom:subscription_status'] ?? u.subscription_status,
        prepaid_minutes:     attributes['custom:prepaid_minutes']     ?? u.prepaid_minutes,
        prepaid_expiry:      attributes['custom:prepaid_expiry']      ?? u.prepaid_expiry,
      };
    }));
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f9fb' }}>
      {/* Nav */}
      <nav className="bg-white shadow-sm" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="font-inter text-sm font-medium px-3 py-2 rounded-md transition-colors"
                style={{ color: '#055efd' }}
                onMouseEnter={e => e.target.style.color = '#0449d4'}
                onMouseLeave={e => e.target.style.color = '#055efd'}
              >
                ← Dashboard
              </Link>
              <h1 className="text-xl font-montserrat font-semibold" style={{ color: '#222324' }}>
                User Manager
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => loadUsers(search, statusFilter)}
                className="font-inter text-sm font-medium px-3 py-2 rounded-md transition-colors"
                style={{ color: '#979ba4' }}
                onMouseEnter={e => e.target.style.color = '#222324'}
                onMouseLeave={e => e.target.style.color = '#979ba4'}
              >
                Refresh
              </button>
              <button
                onClick={signOut}
                className="font-inter text-sm font-medium px-3 py-2 rounded-md transition-colors"
                style={{ color: '#979ba4' }}
                onMouseEnter={e => e.target.style.color = '#222324'}
                onMouseLeave={e => e.target.style.color = '#979ba4'}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="mb-6 px-4 py-3 text-sm font-inter rounded-lg"
              style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
              {error}
            </div>
          )}

          {/* Search bar */}
          <form onSubmit={handleSearch} className="flex gap-3 mb-6">
            <input
              type="text"
              placeholder="Search by email prefix…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 px-4 py-2 text-sm font-inter border rounded-xl"
              style={{ borderColor: '#e5e7eb', color: '#222324', backgroundColor: '#fff' }}
            />
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); loadUsers(search, e.target.value); }}
              className="px-3 py-2 text-sm font-inter border rounded-xl"
              style={{ borderColor: '#e5e7eb', color: '#222324', backgroundColor: '#fff' }}
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s || 'All statuses'}</option>
              ))}
            </select>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-inter font-medium text-white rounded-xl"
              style={{ backgroundColor: '#055efd' }}
            >
              Search
            </button>
          </form>

          {/* Table */}
          <div className="bg-white shadow-sm overflow-hidden" style={{ borderRadius: '16px', border: '1px solid #e5e7eb' }}>
            <div className="px-6 py-5" style={{ borderBottom: '1px solid #e5e7eb' }}>
              <h3 className="text-lg font-montserrat font-semibold" style={{ color: '#222324' }}>
                Cognito Users {!loading && `(${users.length})`}
              </h3>
              <p className="mt-1 text-sm font-inter" style={{ color: '#979ba4' }}>
                User pool: us-east-1_eQ4oSjtGM
              </p>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="font-inter text-sm" style={{ color: '#979ba4' }}>Loading users…</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <p className="font-inter text-sm" style={{ color: '#979ba4' }}>No users found</p>
              </div>
            ) : (
              <ul>
                {users.map((user, index) => {
                  const isExpanded = expandedUser === user.username;
                  const isActing = actionLoading === user.username;
                  return (
                    <li
                      key={user.username}
                      style={{ borderBottom: index < users.length - 1 ? '1px solid #e5e7eb' : 'none' }}
                    >
                      {/* Main row */}
                      <div className="px-6 py-4">
                        <div className="flex items-center justify-between gap-4">
                          {/* Left: identity */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 flex-wrap">
                              <p className="text-sm font-inter font-medium" style={{ color: '#222324' }}>
                                {user.email || user.username}
                              </p>
                              <StatusBadge status={user.status} enabled={user.enabled} />
                              {user.admin === 'true' && (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-inter font-medium"
                                  style={{ backgroundColor: '#eff6ff', color: '#055efd', border: '1px solid #bfdbfe', borderRadius: '10px' }}>
                                  Admin
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex items-center gap-4 text-xs font-inter flex-wrap"
                              style={{ color: '#979ba4' }}>
                              {user.business_id && <span>biz: {user.business_id}</span>}
                              {user.subscription_status && <span>sub: {user.subscription_status}</span>}
                              {user.prepaid_minutes && <span>mins: {user.prepaid_minutes}</span>}
                              <span>Created {formatDate(user.created)}</span>
                            </div>
                          </div>

                          {/* Right: actions */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => setExpandedUser(isExpanded ? null : user.username)}
                              className="px-3 py-1.5 text-xs font-inter font-medium rounded-lg transition-colors"
                              style={{ color: '#055efd', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff' }}
                            >
                              {isExpanded ? 'Hide' : 'Details'}
                            </button>
                            <button
                              onClick={() => setEditingUser(user)}
                              className="px-3 py-1.5 text-xs font-inter font-medium rounded-lg transition-colors"
                              style={{ color: '#222324', border: '1px solid #e5e7eb', backgroundColor: '#fff' }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleEnabled(user)}
                              disabled={isActing}
                              className="px-3 py-1.5 text-xs font-inter font-medium rounded-lg transition-colors disabled:opacity-50"
                              style={{
                                color: user.enabled ? '#ea580c' : '#16a34a',
                                border: `1px solid ${user.enabled ? '#fed7aa' : '#bbf7d0'}`,
                                backgroundColor: user.enabled ? '#fff7ed' : '#f0fdf4',
                              }}
                            >
                              {isActing ? '…' : user.enabled ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              onClick={() => handleDelete(user)}
                              disabled={isActing}
                              className="px-3 py-1.5 text-xs font-inter font-medium rounded-lg transition-colors disabled:opacity-50"
                              style={{ color: '#dc2626', border: '1px solid #fecaca', backgroundColor: '#fef2f2' }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Expanded attributes */}
                      {isExpanded && (
                        <div className="px-6 pb-4" style={{ backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                          <p className="text-xs font-inter font-semibold uppercase mt-3 mb-2" style={{ color: '#979ba4', letterSpacing: '0.05em' }}>
                            All Attributes
                          </p>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                            {Object.entries(user.attributes || {}).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => (
                              <div key={k} className="flex gap-2 text-xs font-inter py-0.5">
                                <span style={{ color: '#6b7280', minWidth: '180px' }}>{k}</span>
                                <span style={{ color: '#222324' }}>{v}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {editingUser && (
        <EditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleSaveAttributes}
        />
      )}
    </div>
  );
}

export default UserManager;
